/**
 * Formulario de tarjeta INLINE (Stripe Card Element, sin redirect):
 * SetupIntent (usage off_session — establece el mandato para cobros
 * posteriores, el mismo mecanismo del happy path 3DS) → confirmCardSetup
 * (presenta el reto 3DS inline si el banco lo pide) → default payment method
 * del customer/suscripción. Reusado por: recuperación en el modal de
 * sucursales y Configuración → Método de pago. Solo SUPER_USER (la API lo
 * exige; el caller decide el mount).
 */
import { useEffect, useRef, useState } from 'react';
import type { Stripe, StripeCardElement } from '@stripe/stripe-js';
import { api } from '../lib/api';
import { getStripe } from '../lib/stripe';
import { useAuth } from '../auth/AuthProvider';

const errMsg = (e: any, fb: string) => e?.body?.message || e?.message || fb;

export function PaymentMethodForm({ onSaved }: { onSaved?: () => void }) {
  const holder = useRef<HTMLDivElement>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [card, setCard] = useState<StripeCardElement | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let el: StripeCardElement | null = null;
    getStripe().then((s) => {
      if (cancelled) return;
      if (!s || !holder.current) { setUnavailable(!s); return; }
      setStripe(s);
      el = s.elements().create('card', { hidePostalCode: true });
      el.mount(holder.current);
      setCard(el);
    });
    return () => { cancelled = true; try { el?.destroy(); } catch { /* ya desmontado */ } };
  }, []);

  const save = async () => {
    if (!stripe || !card) return;
    setBusy(true); setErr(null); setOk(false);
    try {
      const { clientSecret } = await api<{ clientSecret: string }>('/billing/setup-intent', {
        method: 'POST', body: {},
      });
      // 3DS inline aquí si el banco lo exige (confirmCardSetup maneja el reto).
      const result = await stripe.confirmCardSetup(clientSecret, { payment_method: { card } });
      if (result.error) throw new Error(result.error.message ?? 'No se pudo guardar la tarjeta');
      const pm = result.setupIntent?.payment_method;
      const pmId = typeof pm === 'string' ? pm : pm?.id;
      if (!pmId) throw new Error('Stripe no devolvió el método de pago');
      await api('/billing/default-payment-method', { method: 'POST', body: { paymentMethodId: pmId } });
      setOk(true);
      card.clear();
      onSaved?.();
    } catch (e: any) {
      setErr(errMsg(e, 'No se pudo guardar la tarjeta'));
    } finally { setBusy(false); }
  };

  if (unavailable) {
    return (
      <p className="text-xs text-slate-400">
        El formulario de tarjeta no está disponible en este ambiente (falta la configuración de Stripe).
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div ref={holder} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5" />
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {ok && <p className="text-sm text-emerald-600">Tarjeta guardada como método de pago principal ✓</p>}
      <div className="flex justify-end">
        <button
          onClick={save} disabled={busy || !card}
          className="rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
          {busy ? 'Guardando…' : 'Guardar tarjeta'}
        </button>
      </div>
    </div>
  );
}

/** Configuración → Método de pago (admin, con billing vivo): actualización
 *  PROACTIVA de tarjeta — no hay que esperar a que un cobro falle.
 *  AUTO-GATEADO por useAuth (patrón ReferralCard/BranchControls): el gate del
 *  kill-switch vive AQUÍ y no en el mount — el `user` local del Dashboard es
 *  meToUser(me) (solo name/email/role/permissions, SIN billing), y gatear
 *  sobre ese objeto dejaba la card invisible para todos (bug te18). */
export function PaymentMethodSection() {
  const { user } = useAuth() as any;
  const checkoutEnabled = !!user?.billing?.checkoutEnabled;
  const isSuper = user?.role === 'SUPER_USER';

  const openPortal = async () => {
    try {
      const r = await api<{ url: string }>('/billing/portal-session', { method: 'POST', body: {} });
      window.open(r.url, '_blank', 'noopener');
    } catch { /* sin suscripción aún: el portal no aplica */ }
  };

  if (!checkoutEnabled || !isSuper) return null;

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 md:px-8">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Método de pago</h3>
        <p className="text-sm text-slate-500">
          La tarjeta con la que se cobran tu suscripción y las sucursales adicionales.
          Actualízala aquí en cualquier momento — sin salir del app.
        </p>
      </div>
      <div className="space-y-3 px-6 py-5 md:px-8">
        <PaymentMethodForm />
        <button onClick={openPortal} className="text-xs text-slate-400 underline hover:text-slate-600">
          o gestiona tus métodos de pago y facturas en el portal de Stripe
        </button>
      </div>
    </div>
  );
}
